import { ClearwaterTransaction } from '@helios/common-api'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
export const transactionsFeatureKey = 'transactions'

/**
 * Action strings returned by the cw-transactions/availableActions API endpoint.
 * These represent user-level permissions to create specific transaction types.
 */
export const CreateEditTransAction = {
  CreateTrade: 'create-trade',
  CreateCashEntry: 'create-cash-entry',
  CreateFxSpotTrade: 'create-fx-spot-trade',
  CreateCurrencyForward: 'create-currency-forward',
  CreateWireTransfer: 'create-wire-transfer',
  CreateDirectTransfer: 'create-direct-transfer',
  CreateSecurityTransfer: 'create-security-transfer',
} as const

export type TransactionCreatePermissions = {
  createTrade: boolean
  createCashEntry: boolean
  createFxSpotTrade: boolean
  createCurrencyForward: boolean
  createWireTransfer: boolean
  createDirectTransfer: boolean
  createSecurityTransfer: boolean
}

export type TransactionsRedux = {
  transactions: ClearwaterTransaction[]
  selectAllRowsEnabled: boolean
  selectAllExtTxsRowsEnabled: boolean
  commandsMetrics: { command: string; time: number }
  /** Available create-permission action strings from cw-transactions/availableActions */
  createEditTransActions?: string[]
}

export const transactionsInitialState: TransactionsRedux = {
  transactions: [],
  selectAllRowsEnabled: false,
  selectAllExtTxsRowsEnabled: false,
  commandsMetrics: { command: '', time: 0 },
}

export const transactionsSlice = createSlice({
  name: transactionsFeatureKey,
  initialState: transactionsInitialState,
  reducers: {
    setTransactions: (
      oldState,
      action: PayloadAction<{ transactions: ClearwaterTransaction[] }>,
    ) => ({
      ...oldState,
      transactions: action.payload.transactions,
    }),
    clearTransactions: oldState => ({ ...oldState, transactions: [] }),
    setSelectAllRowsEnabled: (
      oldState,
      action: PayloadAction<{ selectAllRowsEnabled: boolean }>,
    ) => ({
      ...oldState,
      selectAllRowsEnabled: action.payload.selectAllRowsEnabled,
    }),
    setSelectAllExtTxsRowsEnabled: (
      oldState,
      action: PayloadAction<{ selectAllExtTxsRowsEnabled: boolean }>,
    ) => ({
      ...oldState,
      selectAllExtTxsRowsEnabled: action.payload.selectAllExtTxsRowsEnabled,
    }),
    setCommandsMetrics: (
      oldState,
      action: PayloadAction<{ command: string; time: number }>,
    ) => ({
      ...oldState,
      commandsMetrics: action.payload,
    }),
    setCreateEditTransActions: (
      oldState,
      action: PayloadAction<{ actions: string[] }>,
    ) => ({
      ...oldState,
      createEditTransActions: action.payload.actions,
    }),
    clearCreateEditTransActions: oldState => ({
      ...oldState,
      createEditTransActions: undefined,
    }),
  },
})

export const {
  setTransactions,
  clearTransactions,
  setSelectAllRowsEnabled,
  setCommandsMetrics,
  setSelectAllExtTxsRowsEnabled,
  setCreateEditTransActions,
  clearCreateEditTransActions,
} = transactionsSlice.actions

export default transactionsSlice.reducer
